import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PROFILE_STYLES } from '../../constants';
import { ReactComponent as AddBusinessIcon } from '../../../../assets/svg/plus-new.svg';
import { ReactComponent as AccountSwitcherIcon } from '../../../../assets/account-switcher/account-switcher.svg';
import Button from '../../../../generic-components/Button';
import Heading from '../../../../generic-components/Heading';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import { ProfilesRender } from './ProfilesRender';
import { useStore } from '../../../../store';
import { AccountSwitcher } from '../account-switcher';
import { AccountantAccountSwitcherMode, IClientResponse } from '../accountant-account-switcher/types';
import { AccountantAccountSwitcher } from '../accountant-account-switcher/AccountantAccountSwitcher';
import { ISummaryData } from '../../../../types';

const ZeroStateBadge = ({ title }: { title: string }) => (
  <Heading title={title} className={PROFILE_STYLES.ACCOUNT_SWITCHER_TRIGGER.ZERO_STATE_BADGE} />
);

export enum AccountSwitcherType {
  BUSINESS,
  AUTHORIZED_USER,
}

interface IProps {
  onCloseDetails: () => void;
  switcherType: AccountSwitcherType;
}

export const AccountSwitchTrigger = ({ onCloseDetails, switcherType }: IProps) => {
  const summaryData: ISummaryData = useStore((state) => state.summary);
  const combinedLists: IClientResponse[] = [
    ...(summaryData?.authorizedUserData?.authorizedUserInvites || []),
    ...(summaryData?.authorizedUserData?.authorizedUserPendingInvites || []),
  ];
  const [shouldShowSwitcher, setShouldShowSwitcher] = useState<boolean>(false);
  const { t } = useTranslation();

  const hasOtherBusinesses = useMemo(() => {
    if (switcherType === AccountSwitcherType.BUSINESS) {
      return !!summaryData.otherBusinesses?.length;
    }
    return !!combinedLists.length;
  }, [summaryData]);

  const getProfilesList = () => {
    if (switcherType === AccountSwitcherType.BUSINESS) {
      return summaryData.otherBusinesses;
    }
    // Build client list
    return [];
  };

  return (
    <div>
      <Button
        id={hasOtherBusinesses ? 'account_switcher' : 'add_new_business'}
        title=''
        className={PROFILE_STYLES.ACCOUNT_SWITCHER_TRIGGER.WRAPPER}
        childElement={
          <div className={PROFILE_STYLES.ACCOUNT_SWITCHER_TRIGGER.CHILDREN_WRAPPER}>
            <div className={PROFILE_STYLES.ACCOUNT_SWITCHER_TRIGGER.CHILDREN_LEFT_CONTAINER}>
              {hasOtherBusinesses ? (
                <AccountSwitcherIcon className='stroke-bw08 w-[24px]' />
              ) : (
                switcherType === AccountSwitcherType.BUSINESS && <AddBusinessIcon className='stroke-bw08 w-[24px]' />
              )}
              <Heading
                id='dynamic_title'
                title={
                  hasOtherBusinesses
                    ? switcherType === AccountSwitcherType.BUSINESS
                      ? t('ACCOUNT_SWITCHER.TITLE')
                      : t('ACCOUNT_SWITCHER.ACCOUNTANT_TITLE')
                    : t('ACCOUNT_SWITCHER.ZERO_STATE')
                }
                className={PROFILE_STYLES.ACCOUNT_SWITCHER_TRIGGER.TRIGGER_TITLE}
              />
            </div>
            {hasOtherBusinesses ? (
              <ProfilesRender businessList={getProfilesList()} />
            ) : (
              <ZeroStateBadge title={t('ACCOUNT_SWITCHER.ZERO_STATE_BADGE')} />
            )}
          </div>
        }
        onClick={() => setShouldShowSwitcher(true)}
      />
      {shouldShowSwitcher &&
        (switcherType === AccountSwitcherType.BUSINESS ? (
          <UserMessage onClick={() => setShouldShowSwitcher(false)} id='AccountSwitcherWrapper'>
            <AccountSwitcher
              onClose={() => {
                setShouldShowSwitcher(false);
                onCloseDetails();
              }}
            />
          </UserMessage>
        ) : (
          <AccountantAccountSwitcher
            mode={AccountantAccountSwitcherMode.PROFILE}
            onClose={() => setShouldShowSwitcher(false)}
          />
        ))}
    </div>
  );
};
